'use client';

import * as Sentry from '@sentry/nextjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MAX_SCHEDULE_FUTURE_DAYS,
  MAX_SCHEDULE_DAYS_TO_FETCH,
  SCHEDULE_DATE_FORMAT,
  INITIAL_SCHEDULE_DAYS,
} from '../classfinder.constants';
import {
  fetchServiceSchedule,
} from '@/common/utils/getServiceSchedule';
import {
  CLASSFINDER_ENTITY_TYPES,
  ClassfinderEntityType,
  ClassSchedule,
  SchedulesResponse,
  ServiceSchedule,
} from '@/widgets/FindAClass/classfinder.interfaces';
import {
  fetchClassSchedule,
} from '@/common/utils/getClassSchedule';
import dayjs from '@/common/lib/dayjs';
import { fetchWithConcurrencyLimit } from '@/common/utils/fetchWithConcurrencyLimit';
import { createDateChunks, getRangeKey, isAbortLikeError, mergeSchedulesByEntityType } from '../utils/scheduleFetchUtils';

interface UseSchedulesProps {
  studioId: string | null;
  lang: string;
  type: ClassfinderEntityType;
}

export function useSchedules({ studioId, lang, type }: UseSchedulesProps) {
  const [schedules, setSchedules] = useState<
    Map<number, ClassSchedule | ServiceSchedule>
  >(new Map());
  const [isFetchingSchedules, setIsFetchingSchedules] = useState(false);
  const [endDate, setEndDate] = useState<dayjs.Dayjs>(
    dayjs()
      .startOf('day')
      .add(INITIAL_SCHEDULE_DAYS - 1, 'day')
  );
  const startDateRef = useRef(dayjs().startOf('day'));
  const fetchedRangesRef = useRef<Set<string>>(new Set());

  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchSchedulesForRange = useCallback(
    async (start: dayjs.Dayjs, end: dayjs.Dayjs) => {
      const chunks = createDateChunks(start, end);

      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setIsFetchingSchedules(true);

      try {
        const fetchFunction =
          type === CLASSFINDER_ENTITY_TYPES.CLASSES ? fetchClassSchedule : fetchServiceSchedule;

        const responses = await fetchWithConcurrencyLimit(
          chunks.map(
            ([chunkStart, chunkEnd]) =>
              () =>
                fetchFunction({
                  clubId: Number(studioId),
                  lang,
                  start_date: chunkStart.format(SCHEDULE_DATE_FORMAT),
                  end_date: chunkEnd.format(SCHEDULE_DATE_FORMAT),
                  signal: controller.signal,
                }) as Promise<SchedulesResponse>
          ),
          1
        );

        if (controller.signal.aborted) {
          return;
        }

        fetchedRangesRef.current.add(getRangeKey(start, end));
        setSchedules((prevSchedules) =>
          mergeSchedulesByEntityType(prevSchedules, responses, type)
        );

      } catch (error: unknown) {
        if (isAbortLikeError(error)) {
          console.log('Fetch was aborted, not updating schedules');
        } else {
          Sentry.captureException(error);
          console.warn('Fetch schedules error:', error);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsFetchingSchedules(false);
        }
      }
    },
    [studioId, lang]
  );

  useEffect(() => {
    if (!studioId) return;
    const today = dayjs().startOf('day');
    const endDate = today.add(INITIAL_SCHEDULE_DAYS - 1, 'day');
    startDateRef.current = today;
    setEndDate(endDate);
    fetchSchedulesForRange(today, endDate);
  }, [studioId, fetchSchedulesForRange]);

  const handleLoadMore = useCallback(async (): Promise<
    dayjs.Dayjs | undefined
  > => {
    const maxEndDate = dayjs()
      .startOf('day')
      .add(MAX_SCHEDULE_FUTURE_DAYS - 1, 'day');

    if (endDate.isSameOrAfter(maxEndDate, 'day')) {
      return;
    }

    // Calculate next range
    const nextStartDate = endDate.add(1, 'day');
    const proposedNewEndDate = nextStartDate.add(
      MAX_SCHEDULE_DAYS_TO_FETCH - 1,
      'day'
    );
    const newEndDate = proposedNewEndDate.isAfter(maxEndDate)
      ? maxEndDate
      : proposedNewEndDate;

    // Check if we've already fetched this range
    const rangeKey = getRangeKey(nextStartDate, newEndDate);
    if (fetchedRangesRef.current.has(rangeKey)) {
      setEndDate(newEndDate);
      return nextStartDate;
    }

    await fetchSchedulesForRange(nextStartDate, newEndDate);
    setEndDate(newEndDate);
    return nextStartDate;
  }, [endDate, fetchSchedulesForRange]);

  const fetchSchedulesForDate = useCallback(
    async (date: dayjs.Dayjs) => {
      const today = dayjs().startOf('day');
      const maxEndDate = today.add(MAX_SCHEDULE_FUTURE_DAYS - 1, 'day');

      if (date.isBefore(today) || date.isAfter(maxEndDate)) {
        return;
      }

      // Calculate the 14-day range that includes the selected date
      const rangeStart = date.startOf('day');
      const rangeEnd = rangeStart
        .add(MAX_SCHEDULE_DAYS_TO_FETCH - 1, 'day')
        .isAfter(maxEndDate)
        ? maxEndDate
        : rangeStart.add(MAX_SCHEDULE_DAYS_TO_FETCH - 1, 'day');

      // Check if we've already fetched this range
      const rangeKey = getRangeKey(rangeStart, rangeEnd);
      if (fetchedRangesRef.current.has(rangeKey)) {
        setEndDate(rangeEnd);
        return;
      }

      await fetchSchedulesForRange(rangeStart, rangeEnd);
      setEndDate(rangeEnd);
    },
    [fetchSchedulesForRange]
  );

  return {
    handleLoadMore,
    fetchSchedulesForDate,
    schedules: Array.from(schedules.values()),
    isFetchingSchedules,
    currentStartDate: startDateRef.current,
    currentEndDate: endDate,
    canLoadMore: endDate.isBefore(
      dayjs()
        .startOf('day')
        .add(MAX_SCHEDULE_FUTURE_DAYS - 1, 'day'),
      'day'
    ),
  };
}
